/**
 * Chat UI: DMs, groups, composer, moderation hooks for media.
 */
export const conversations = {
    contextMenu: {
        copy: 'Copy',
        copyAll: 'Copy all',
        selectAll: 'Select all',
        paste: 'Paste',
        copyGifLink: 'Copy GIF link',
        copyStickerLink: 'Copy sticker link',
        copySelection: 'Copy selection',
        copyMessage: 'Copy message',
        copyLink: 'Copy link',
        copyImage: 'Copy image',
        download: 'Download',
        copied: 'Copied',
        copyFailed: 'Could not copy to clipboard',
        pasteFailed: 'Could not paste from clipboard',
        downloadFailed: 'Could not download file',
        fileSaved: 'File saved',
        mediaNotReady: 'This attachment is not ready to download yet.',
        archive: 'Archive',
        unarchive: 'Unarchive',
        keepArchived: 'Keep archived on new messages',
        addFavorite: 'Add to Favourites',
        removeFavorite: 'Remove from Favourites',
        editConversation: 'Edit Conversation',
    },

    mediaLoading: 'Loading...',
    mediaUploading: 'Uploading...',
    mediaScanning: 'Awaiting moderation...',
    /** Composer attachment chip while the file is uploading to storage. */
    composerAttachmentUploading: 'Uploading',
    /** Composer attachment chip while moderation scan runs (after upload completes). */
    composerAttachmentModerating: 'Moderating',
    mediaRejected: 'This content has been removed (auto-moderation)',
    mediaModerationError: 'This content could not be verified and has been hidden for safety',
    mediaError: 'Failed to load media',
    expandMedia: 'Click to expand',
    /** Composer attach control (images + video). */
    attachMedia: 'Attach image or video',
    /** Paste looked like media but we could not read bytes (e.g. some app clipboard formats). */
    pasteMediaUnreadableTitle: 'Could not use clipboard content',
    pasteMediaUnreadableDesc:
        'We noticed a paste that may include an image or video, but could not read usable media. Try saving the file and attaching it, or copying from another app.',
    /** Conversation drag-and-drop overlay. */
    dropFilesToAttach: 'Drop to attach',
    dropFilesToAttachHint: 'Drag and drop media or files to attach',
    mediaLightbox: 'Image preview',
    group: 'Group',
    members: 'Members',
    directMessage: 'Direct message',
    /** Sidebar hover: e.g. "120 messages" (count from GET /conversations/:id). */
    sidebarHoverMessageCount: '{{count}} messages',
    /** Sidebar hover: second line; `left` is "N members" or "N participants", `right` is message count or an ellipsis while loading. */
    sidebarHoverMetaBulletMessages: '{{left}} • {{right}}',
    /** Sidebar hover: `right` when message count still loading. */
    sidebarHoverMessagesLoading: '…',
    /** Sidebar hover: start date under meta line. */
    sidebarHoverSince: 'Since {{date}}',
    headerLatestPinLoading: 'Loading…',
    headerLatestPinUnavailable: 'Pinned message unavailable',
    headerLatestPinSystem: 'System message',
    headerLatestPinTooltip: 'Open the newest pinned message (by send time)',
    settings: 'Settings',
    settingsTabPersonal: 'Personal',
    settingsTabConversation: 'Group-Wide',
    leave: 'Leave',
    you: 'You',
    admin: 'Admin',
    makeAdmin: 'Make Admin',
    removeMember: 'Remove',
    memberMenu: {
      mention: 'Mention',
      mentionDisabled: 'You cannot mention here.',
      copyUserId: 'Copy User ID',
      copyUserIdSuccess: 'User ID copied.',
      copyUserIdFailed: 'Could not copy User ID.',
      nicknameAndColor: 'Nickname & Color',
      removeTitle: 'Remove member?',
      removeConfirm: 'Remove {{name}} from this conversation?',
      removeFailed: 'Could not remove member.',
      promoteFailed: 'Could not make member an admin.',
      removeDisabledSelf: 'You cannot remove yourself.',
      removeDisabledAdmin: 'You cannot remove another admin.',
    },
    invitedSection: 'Invited',
    revokeInvite: 'Revoke invite',
    revokeInviteFailed: 'Could not revoke the invite.',
    notFound: 'Conversation not found',
    loadMessageContextFailed: 'Could not load messages',
    backHome: 'Back to home',
    deleteGroup: 'Delete Group',
    deleteConversation: 'Delete conversation',
    reactionSaveError: 'Could not save your reaction.',
    reactionRemoveError: 'Could not remove your reaction.',
    react: 'React',
    reactFavoriteHint: '· React · Shift+click to remove',
    deleteForMe: 'Delete for me',
    deleteForEveryone: 'Delete for everyone',
    moreActions: 'More actions',
    pinnedMessages: 'Pinned messages',
    pinnedMessage: 'Pinned',
    pinMessage: 'Pin message',
    unpinMessage: 'Unpin message',
    removePinTooltip: 'Remove Pin',
    pinFailed: 'Could not pin message',
    unpinFailed: 'Could not unpin message',
    pins: 'Pins',
    closePinsPanel: 'Close pinned messages',
    moreOptions: 'More options',
    pinsEmpty: 'No pinned messages yet.',
    pinsCouldNotLoad: 'Could not load pins.',
    loadPinnedFailed: 'Could not load pinned messages',

    leaveGroup: {
      title: 'Leave group?',
      confirm: "You won't be able to rejoin without a new invite.",
      lastMember: 'You are the last member. The group and all messages will be permanently deleted.',
      confirmBtn: 'Leave',
    },

    deleteGroupDialog: {
      title: 'Delete group?',
      confirm: 'This will permanently delete the group and all messages for everyone. Not reversible.',
      confirmBtn: 'Delete',
    },

    deleteConversationDialog: {
      title: 'Delete conversation?',
      confirm: 'This will permanently delete the conversation and all messages for everyone.',
      confirmBtn: 'Delete',
    },

    editMember: 'Edit member',
    nickname: 'Nickname',
    nicknamePlaceholder: 'Custom name...',
    memberColor: 'Colour',
    clearNickname: 'Clear nickname',
    clearColor: 'Clear colour',
    saveMemberSettings: 'Save',
    cancelMemberSettings: 'Cancel',
    colorDisplayMode: 'Color Display Options',
    colorDisplayName: 'Name',
    colorDisplayAvatarAccent: 'Avatar Accent',
    colorDisplayMessageBorder: 'Message Border',
    colorDisplayNameOnly: 'Name only',
    colorDisplayNameAccent: 'Name + avatar accent',
    colorDisplayNameBubble: 'Name + message tint',
    memberSettingsUpdated: 'Member settings updated',

    memberSecurity: {
      link: 'Device Signatures',
      title: 'Device Signatures — {{name}}',
      titleSelf: 'Your device signatures',
      toolbarTooltip: 'Open your device signatures',
      toolbarAria: 'Device signatures',
      summary:
        'Below are {{name}}\'s device trust fingerprints. Each line is stable for that device until they change keys, and is the same in every conversation. You can mark a device verified to compare against future messages.',
      summarySelf:
        'Below are your device trust fingerprints. Others can compare these lines to what they see for you in any chat. Each line stays the same until you replace keys on that device.',
      accordionTitle: 'Would you like to know more?',
      introP1:
        'All DM and group conversations in Adieuu are end-to-end encrypted, meaning the messages are encrypted on user devices before they reach Adieuu servers. Each different device (phone, desktop, etc) a user logs in with has its own keys that it encrypts and signs messages with.',
      introP1Self:
        'All DM and group conversations in Adieuu are end-to-end encrypted, meaning the messages are encrypted on user devices before they reach Adieuu servers. Each different device (phone, desktop, etc) you use has its own keys to encrypt and sign messages.',
      introP2:
        'Much like a wax seal on an envelope, different signatures might indicate that someone tampered with {{name}}\'s messages, or tried to impersonate them.',
      introP2Self:
        'Much like a wax seal on an envelope, a signature that does not match what others expect might mean someone tampered with your messages, or tried to impersonate you.',
      introP3:
        'What to do: ask {{name}} to open Device Signatures in their app. Read the full fingerprint lines to each other (voice/video or in-person if you want to be careful). The same device shows the same line in every conversation.',
      introP3Self:
        'What to do: when the other person is ready, read your fingerprint lines aloud so they can confirm they match what they see for your devices in their app. Your lines are the same in every chat until you change keys on a device.',
      introP4:
        'If the signatures do not match, it\'s possible that device (or its messages) are compromised.',
      introP4Self:
        'If the signatures do not match, it\'s possible that your device is compromised, or your messages are otherwise being tampered with before reaching the others.',
      devicesHeading: 'Signatures by device',
      deviceListBlurb:
        'Each block below is one device. Agree which block you are comparing (the device id can help), then check that the full line matches exactly what {{name}} sees for that device—the line is identical in any conversation.',
      deviceListBlurbSelf:
        'Each block below is one of your devices. Agree which block you are comparing (the device id can help), then read your full line aloud so the other person can confirm it matches what they see for you anywhere in the app.',
      deviceOrdinal: 'Device {{n}}',
      deviceIdCaption: 'Device ID v{{version}}',
      loadError: 'Could not load security information.',
      noDevices: 'No devices are registered for this person yet, so there is nothing to compare (this is unlikely if the issue persists, contact Support).',
      noDevicesSelf:
        'No devices are registered for your account yet, so there is nothing to compare (this is unlikely if the issue persists, contact Support).',
      codeCaption: 'Signature (compare the full line)',
      spkUnavailable:
        'We cannot show a signature for this device from here. Ask them to read their code from their own app, or try again later.',
      verifyFailed:
        'We could not confirm this device\'s code from here. Be cautious until you can verify another way.',
      attestationPending:
        'No trust fingerprint yet for this device—{{name}} needs to update to the latest app and sign in so their device can publish a signature.',
      attestationPendingSelf:
        'No trust fingerprint for this device yet. Update to the latest app and stay signed in; your device will publish a signature automatically.',
      copied: 'Signature copied',
      copyFailed: 'Could not copy to clipboard',
      markVerified: 'Verified',
      verifyPersistFailed: 'Could not update verification',
      fingerprintMatchIndicator:
        'This message came from a device you verified.',
      fingerprintMismatchIndicator:
        'Verified fingerprint no longer matches this device. Keys may have changed.',
      trustBlockWarnSelf:
        'A new or changed device was added for this alias. Review your devices before sharing anything sensitive.',
      trustBlockWarnOther:
        '{{names}} added or changed a device. Review it before sharing anything sensitive.',
      trustBlockWarnMixed:
        'A new or changed device was added for this alias and for {{names}}. Review devices before sharing anything sensitive.',
      trustBlockPauseNote:
        'Sending and calling stay paused until you accept the new devices.',
      reviewFingerprints: 'Review',
      acceptDevices: 'Accept these devices',
      acceptDevicesHint:
        'This pins the devices shown above so you can send and call. Compare fingerprints first if you can.',
      devicesAccepted: 'Devices accepted',
      acceptFailed: 'Could not accept these devices',
    },

    signatureUnverifiedBanner:
        'This message\'s signature could not be verified. It may not have been sent by the displayed sender, or may have been moved from another conversation.',
    signatureUnverifiedShow: 'Show message',
    signatureUnverifiedHide: 'Hide message',
    signatureUnverifiedPreview: 'Unverified message',
    signatureUnverifiedOptions: 'Options',
    signatureChecking: 'Checking this message\'s signature…',

    signatureRefreshNotice: {
      body_one:
        'Signing keys for {{names}} were refreshed. 1 message that was shown as unverified is now verified.',
      body_other:
        'Signing keys for {{names}} were refreshed. {{count}} messages that were shown as unverified are now verified.',
      review: 'Device Signatures',
    },

    signatureOptions: {
      title: 'Unverified signature',
      intro:
        'Every message is signed by the device that sent it. This message\'s signature did not check out, so its contents are hidden until you choose to show them.',
      causeTitle: 'What might have happened',
      cause: {
        pendingKey:
          'This app does not have the sender\'s newest signing key yet. This usually happens after they set up a device or update the app, and checking again often fixes it.',
        outsideMembers:
          'The signature is valid, but the message was also encrypted for someone who is not in this conversation\'s member list on this device. Review the members and their Device Signatures before trusting it.',
        generic:
          'The signature does not match the sender\'s published keys. The message may have been changed, sent by someone else, or moved from another conversation. An out-of-date app on the sender\'s side can also cause this.',
      },
      deviceVerificationNote:
        'Marking a device as Verified in Device Signatures confirms its fingerprint. It does not change whether a single message\'s signature checks out.',
      status: {
        verified: 'Signature verified. The warning has been removed.',
        noKey:
          'We still could not get the sender\'s signing key. Try again later, or reload the app.',
        stillFailed: 'The signature still does not match. Treat this message with caution.',
        error: 'Could not check again. Try again later.',
      },
      actions: {
        recheck: 'Check again',
        checking: 'Checking…',
        recheckHint: 'Fetch the sender\'s latest keys and check this message again.',
        deviceSignatures: 'Device Signatures',
        deviceSignaturesHint:
          'Compare {{name}}\'s device fingerprints with them in person or over another trusted channel.',
        details: 'Message details',
        detailsHint: 'See the technical signature and encryption details.',
        reload: 'Reload app',
        reloadHint: 'Restart the app on this device and check every message again with fresh keys.',
        reloadNow: 'Reload now',
        report: 'Report message',
        reportHint: 'Report this message if you think someone is impersonating the sender.',
      },
      reloadConfirm:
        'Reloading ends any call you are in and clears text you have not sent yet.',
    },

    messageDetails: {
        menuItem: 'Message details',
        title: 'Message details',
        description:
            'Technical details about how this message was encrypted and signed. Compare these with someone you trust if something looks unexpected.',
        fieldHintAria: 'About {{label}}',
        copyValue: 'Copy {{label}}',
        advancedTitle: 'Advanced',
        copySignature: 'Copy signature (base64)',
        noSignature: 'No signature on this message',
        searchLabel: 'Filter details',
        searchPlaceholder: 'Filter field names or values',
        noMatches: 'No fields match “{{query}}”.',
        fingerprint: {
            match: 'Fingerprint matches the one you verified for this device.',
            mismatch: 'Fingerprint does not match the one you verified. Keys may have changed.',
            unverified: 'This device is in the sender’s published list, but you have not marked it verified yet.',
            unknownDevice: 'This device is not in the sender’s currently published devices.',
            unavailable: 'A trust fingerprint is not available for this device yet.',
            openSignatures: 'Open Device Signatures',
        },
        version: {
            current: 'Latest / most capable version in this app.',
            newer: 'A newer version is available ({{latest}}).',
            how: {
                relogin:
                    'Log out and sign back in so this device can publish the newer keys. New messages will use it; this message cannot be upgraded in place.',
                reloginSender:
                    'The sender can log out and sign back in to start using it. This message cannot be upgraded in place.',
                newMessages:
                    'New messages from an up-to-date app use it automatically. This historical value cannot be changed.',
            },
        },
        sections: {
            identity: 'Alias',
            timing: 'Time',
            trust: 'Trust',
            encryption: 'Encryption',
            wrapping: 'Key wrapping',
            cipher: 'Community cipher',
        },
        labels: {
            messageId: 'Message ID',
            conversationId: 'Conversation ID',
            spaceId: 'Space ID',
            channelId: 'Channel ID',
            sender: 'Sender',
            senderId: 'Sender ID',
            senderDeviceId: 'Sender device ID',
            clientMessageId: 'Client message ID',
            sent: 'Sent',
            lastEdited: 'Last edited',
            expires: 'Expires',
            revisionCount: 'Edit count',
            signatureStatus: 'Signature check',
            cryptoProfile: 'Crypto profile',
            x25519: 'Classical key agreement',
            kem: 'Post-quantum key encapsulation',
            symmetric: 'Message encryption',
            kdf: 'Key derivation',
            authenticity: 'This message’s authenticity',
            signatureBinding: 'Signature binding',
            forwardSecrecy: 'Forward secrecy',
            fsDowngrade: 'Forward-secrecy fallback',
            yourWrap: 'How this device unwrapped the key',
            wrapVersion: 'Wrap format',
            signedPreKeyId: 'Signed pre-key ID',
            oneTimePreKeyId: 'One-time pre-key ID',
            cipherId: 'Cipher fingerprint',
            cipherMatch: 'Matches your local cipher',
            encryption: 'Encryption',
            hybridPrefix: 'Hybrid encoding prefix',
            signatureBytes: 'Signature size',
            recipientWrap: 'Recipient wraps',
            decryptionError: 'Decryption note',
        },
        values: {
            authenticityHybrid: 'Ed25519 + ML-DSA-65 (hybrid)',
            authenticityEd25519: 'Ed25519 (legacy)',
            authenticityInvalid: 'Unrecognized signature',
            authenticityNone: 'No signature',
            verified: 'Verified',
            failed: 'Failed',
            unsigned: 'Unsigned (legacy)',
            on: 'Yes',
            off: 'No',
            yes: 'Yes',
            v1: 'v1 (ciphertext only)',
            v2: 'v2 (bound to this conversation)',
            unknown: 'Unknown',
            notApplicable: 'Not applicable',
            plaintext: 'Plaintext channel',
            match: 'Yes',
            mismatch: 'No',
            static: 'Static device key',
            spk: 'Signed pre-key',
            otpk: 'One-time pre-key',
            legacy: 'Legacy (no AEAD binding)',
        },
        hints: {
            cryptoProfile:
                "A named suite of algorithms this conversation agreed to use. \"Default\" is Adieuu's shipped mix of classical and post-quantum algorithms. Other profiles (including CNSA 2.0 and custom suites) will be made available in the future. Because post-quantum algorithms are relatively new and may contain unknown weaknesses, we use a 'hybrid' approach that combines both classical and post-quantum signatures: if a PQC algorithm later proves vulnerable, messages are still protected by the classical suites.",
            x25519:
                "X25519 is the classical Diffie-Hellman step. Today's computers cannot break it in practice, but a future quantum computer could. Adieuu therefore also runs a post-quantum encapsulation so both must be broken to recover the message key.",
            kem:
                'ML-KEM (Kyber) is the post-quantum half of key agreement. Even if X25519 were broken by a quantum computer, an attacker still needs to break this encapsulation to read the session key.',
            symmetric:
                'Once devices share a session key, the message body is encrypted with an authenticated cipher (ChaCha20-Poly1305, for example). If decryption fails, this may be a sign of tampering.',
            kdf:
                'HKDF turns the shared secrets into the actual message key. Using a strong KDF keeps keys for different purposes from colliding if one piece of key material is reused.',
            authenticityHybrid:
                'This message was signed with both Ed25519 and ML-DSA-65 over the same data. Both signatures must check out, so a future quantum break of Ed25519 alone is not enough to forge it. This is the current default for new sends.',
            authenticityEd25519:
                'This message has a 64-byte Ed25519 signature only. That is valid history from before hybrid signing, or from a device that had no post-quantum signing key yet. New messages should show Ed25519 + ML-DSA-65.',
            authenticityInvalid:
                'The signature bytes are not a known Ed25519 or hybrid encoding. Treat the authenticity check as failed and do not trust the sender attribution.',
            authenticityNone:
                'There is no alias signature on this message. Older history may be unsigned. New messages are always signed.',
            signatureVerified:
                "The sender's public keys successfully verified the signature over this ciphertext. That does not prove you have compared device fingerprints in person, but it does mean the bytes were not swapped for a different sender's keys.",
            signatureFailed:
                'The signature did not verify. The body may still decrypt, but the displayed sender might be wrong, or the message may have been moved from another conversation. Do not treat it as authentic.',
            signatureUnsigned:
                'This message has no signature to check. That happens on some older history. It is not the same as a failed check — there is simply nothing to verify.',
            bindingV1:
                'Version 1 signed only the ciphertext and wrapped keys. A malicious server could replay that signed blob into a different conversation between the same people. Newer messages use v2 to prevent that.',
            bindingV2:
                'Version 2 binds the signature to this conversation, sender, and client message ID. A server cannot take a valid signature and replay the same ciphertext as a different message elsewhere.',
            bindingSpace:
                'Space messages are signed over the space, channel, sender, client message ID, ciphertext, nonce, and cipher fingerprint. That stops a relay from moving a signed post into another channel or Space.',
            forwardSecrecyOn:
                'This device unwrapped the message with a pre-key (signed or one-time), not the long-term static device key. If this device is stolen later, that older message key is much harder to recover. The message is still end-to-end encrypted either way.',
            forwardSecrecyOff:
                "This device used the long-term static key to unwrap the message. The message is still end-to-end encrypted, but if this device's static keys are stolen later, this message can be decrypted. Turn on forward secrecy to prevent this (note that by its nature, forward secrecy means that a message's key effectively expires, removing any real possiblity of long-term message history).",
            fsDowngrade:
                'The sender asked for forward secrecy, but at least one recipient device had no usable pre-keys, so that device was wrapped with a static key instead. Others may still have forward secrecy.',
            wrapStatic:
                "The session key was wrapped to this device's long-term static keys. This is the most convenient form of E2E encryption, and allows for long-term message history to be preserved, but risks a device compromise revealing the long-term keys and all history being readable. Forward secrecy is a safer option, but by its nature prevents long-term history.",
            wrapSpk:
                'A signed pre-key (SPK) is a medium-lived key the device publishes and rotates. Wrapping to an SPK gives forward secrecy without consuming a one-time key. The SPK id shown is the exact key that was used.',
            wrapOtpk:
                'A one-time pre-key is used at most once, then discarded. That is the strongest forward-secrecy wrap: even the sender cannot reuse that wrap, and a later device compromise should not recover this message key.',
            wrapVersion:
                'Wrap format 2 binds pre-key IDs and related public values into the encryption tag. Stripping those fields later cannot produce a still-valid unwrap. That blocks downgrade tricks against newer clients.',
            wrapVersionLegacy:
                'This wrap has no format version, so metadata was not bound into the encryption tag. Newer wraps use version 2. Legacy unwraps still work for history.',
            cipherId:
                "The cipher fingerprint is a public id derived from the Space's shared key. It lets clients check they are using the same community cipher without revealing the key itself.",
            cipherMatch:
                'The fingerprint on the message matches the community cipher stored on this device. You and the sender used the same shared key for this post.',
            cipherMismatch:
                'The message names a different cipher fingerprint than the one on this device. You may have the wrong cipher linked, or the sender used another key. The body may not decrypt correctly.',
            cipherUnknown:
                "This device does not have a linked community cipher, so the algorithm suite is inferred only from the ciphertext fields. Link the Space's cipher to see the full suite.",
            senderDeviceId:
                "The sending app records which of the sender's devices produced this message. This screen checks that device's fingerprint against Device Signatures when keys are available. A new device id is not automatically a compromise, but a changed fingerprint on a device you already verified is.",
            clientMessageId:
                'A UUID the sending client created. Edits keep the same client message ID so signatures stay bound to the original send.',
            hybridPrefix:
                'Hybrid signatures start with the version tag adieuu-hsig-v1 so a 64-byte Ed25519 verifier cannot treat a truncated hybrid blob as a classical signature.',
            signatureSize:
                'Ed25519 signatures are 64 bytes. Hybrid signatures are 3387 bytes (version prefix + Ed25519 + ML-DSA-65). A surprising size usually means an invalid or corrupted signature.',
            recipientWraps:
                'Each recipient alias gets its own wrapped copy of the session key. This count is how many wraps are on the envelope. Types (static / SPK / OTPK) are listed in Advanced — not the key bytes.',
            decryptionError:
                'This client could not decrypt the body. Common causes are missing device keys, an unshipped crypto profile, or a wrap aimed at another device. Encryption metadata may still be visible.',
        },
    },

    settingsFs: 'Forward Secrecy',
    settingsFsHint: 'Default messages in this conversation to use forward secrecy. Messages without FS remain end-to-end encrypted but persist in history.',
    settingsRenameTitle: 'Conversation topic or name',
    settingsRenamePlaceholder: 'Enter new conversation topic or name...',
    conversationTopicOrNamePlaceholder: 'Conversation topic or name (optional)',
    startSeparateDmLabel: 'Start a new conversation? (must add topic name)',
    firstDmWithFriendNote:
      'You have not messaged this person in a direct conversation before.',
    settingsRenameSave: 'Save',

    adminTransfer: {
      title: 'Choose a new admin',
      description: 'You are the last admin. Choose who should take over before you leave.',
      oldest: 'Oldest member',
      oldestHint: 'The member who joined earliest',
      mostActive: 'Most active member',
      mostActiveHint: 'The member who sent the most messages',
      manual: 'Choose a member',
      manualHint: 'Select a specific member to promote',
      skip: 'Skip',
      leave: 'Leave',
    },

    systemMessage: {
      memberJoined: '{{name}} has joined the conversation',
      memberInvited: '{{actor}} invited {{name}} to the group',
      memberInvitedLine: '{{invitee}} was invited by {{actor}}',
      memberInvitedInviteeOnly: '{{invitee}} was invited',
      memberLeft: '{{name}} has left the conversation',
      adminPromoted: '{{actor}} made {{name}} an admin',
      adminPromotedSimple: '{{name}} is now an admin',
      groupRenamed: '{{actor}} updated the conversation topic',
      groupRenamedSimple: '{{name}} updated the conversation topic',
      settingsUpdated: "{{actor}} set '{{setting}}' to {{value}}",
      settingsUpdatedGeneric: '{{actor}} updated the conversation settings',
      settingValueEnabled: 'enabled',
      settingValueDisabled: 'disabled',
      callStarted: '{{name}} started a call',
      callJoined: '{{name}} joined the call',
      callLeft: '{{name}} left the call',
      callLeftEnded: '{{name}} left the call, and the call was ended',
      callEnded: '{{name}} ended the call',
    },

    invites: {
      panelTitle: 'Chat Invitations',
      typeGroup: 'Group',
      group: 'Group',
      groupNameHidden: 'Conversation topic hidden',
      inviterAndOthers: '{{name}} + {{count}} others',
      inviterAndOthers_one: '{{name}} + 1 other',
      inviterAndOthers_other: '{{name}} + {{count}} others',
      inviterGroup: "{{name}}'s Group",
      invitedBy: 'From {{name}}',
      invitedByLabel: 'Invited by',
      memberCount: '{{count}} members',
      memberCount_one: '1 member',
      memberCount_other: '{{count}} members',
      accept: 'Accept',
      decline: 'Decline',
      noInvites: 'No pending invitations',
      previewMemberCount: '{{count}} members',
      previewMemberCount_one: '1 member',
      previewMemberCount_other: '{{count}} members',
      previewMembers: 'Members',
      previewRecentMembers: 'Recent Members',
      previewUnavailable: 'Preview unavailable',
      alsoInvited: 'Also Invited',
      moreMembers: '+{{count}} more',
      moreMembers_one: '+1 more',
      moreMembers_other: '+{{count}} more',
      othersInvited: '+{{count}} others invited',
      othersInvited_one: '+1 other invited',
      othersInvited_other: '+{{count}} others invited',
    },

    addMember: 'Add Member',

    inviteMember: {
      title: 'Invite Member',
      button: 'Invite Member',
      invite: 'Invite',
      privacyNote:
        'Invitees will be able to see current and invited member lists, but the conversation topic or name will be hidden until they join.',
      previouslyLeft: 'Previously left',
      noEligible: 'No friends available to invite',
      createNew: 'Create New Conversation Instead',
      statusMember: 'Member',
      statusInvited: 'Invited',
      failed: "Couldn't send that invite.",
      targetCooldown:
        'This person was already invited twice in the last 24 hours. Wait before inviting them again.',
    },

    upgradeForOlderMessages: 'Upgrade to view older messages',
    upgradeForOlderMessagesDescription: 'Free accounts can access the most recent 14 days of message history.',

    newUnreads: 'New messages',
    jumpToLatest: 'Jump to latest message',
    jumpToLatestLabel: 'Latest',
    jumpToLatestWithUnread: 'Jump to latest message, {{count}} unread',

    reply: 'Reply',
    cancelReply: 'Cancel reply',
    replyOriginal: 'Original message',
    replyDeleted: 'Message deleted',
    replySystem: 'System message',

    /** Default composer field when a playful placeholder verb is not active. */
    messagePlaceholder: 'Type a message…',
    /** Banner while changing an already-sent message. */
    editingMessage: 'Editing',
    /** Dismiss inline edit in the composer. */
    cancelEdit: 'Cancel edit',
    /** Text-only v1: media blocks entering edit mode (toast + in-composer). */
    editNoAttachments: 'Only text can be edited. Remove attachments first.',
    /** Context menu and message action bar. */
    editMessage: 'Edit',
    /** Inline label next to the sent time after an E2E replace. */
    messageEdited: 'Edited',
    /** Max E2E revisions: toast, disabled pen control, and context `title` when the row is non-actionable. */
    messageEditMax: 'Edit limit reached. Send a new message.',
    /** Optimistic send row: status line until the server confirms the message. */
    pendingSend: {
      sending: 'Sending…',
      failed: 'Failed to send.',
      retry: 'Retry',
      discard: 'Delete',
      cancel: 'Cancel',
      blocked: 'Message could not be sent.',
    },
    /** Rare: “Edited” control has no `lastEditedAt` (native `title` hint). */
    viewEditHistory: 'History',
    /** Popover heading for prior ciphertext snapshots. */
    editHistoryTitle: 'Earlier versions',
    editHistoryLoading: 'Loading…',
    loadEditHistoryFailed: "Couldn't load earlier versions",
    editHistoryEmpty: 'No earlier versions',
    /** Row label: `n` is 1-based, oldest prior version first. */
    editHistoryVersion: 'v{{n}}',
    editHistoryUnableDecrypt: "Can't decrypt this version",
    editHistoryNoPlaintext: 'No text',
    editHistoryNoText: '—',

    notifications: {
      groupInvite: 'Group invitation',
      groupInviteNameHidden:
        "You've been invited to a group (name hidden until you join)",
      groupInviteFromBody: '{{name}} + {{count}} others invited you',
      groupInviteFromBody_one: '{{name}} + 1 other invited you',
      groupInviteFromBody_other: '{{name}} + {{count}} others invited you',
      groupInviteFromSolo: '{{name}} is inviting you',
      groupTerminated: 'Conversation deleted',
      groupTerminatedBody: '{{name}} deleted the conversation',
      memberJoined: 'Member joined',
      memberJoinedBody: '{{name}} joined the group',
      memberAdded: 'Member added',
      memberAddedBody: '{{name}} was added to the group',
      memberLeft: 'Member left',
      memberLeftBody: '{{name}} left the group',
      memberRemoved: 'Member removed',
      memberRemovedBody: '{{name}} was removed from the group',
      groupRenamed: 'Conversation updated',
      groupRenamedBody: 'The conversation topic or name was updated',
      groupRenamedByBody: '{{name}} updated the conversation topic',
      conversationTopicUpdated: 'Conversation updated',
      conversationTopicUpdatedBody: 'The conversation topic or name was updated',
      conversationTopicUpdatedByBody: '{{name}} updated the conversation topic',
      conversationSettingsUpdated: 'Conversation settings',
      conversationSettingsUpdatedBody: "{{name}} set '{{setting}}' to {{value}}",
      adminPromoted: 'New admin',
      adminPromotedBody: '{{name}} was promoted to admin',
    },

    placeholderVerbs: {
      message: '💬 Message',
      hi: '💬 Say Hi to',
      // ping: '🔔 Ping',
      // poke: '👉 Poke',
      // nudge: '👆 Nudge',
      // sendLove: '💖 Send love to',
      // whisper: '👂 Whisper to',
      // shout: '🔊 Shout at',
      // wave: '👋 Wave at',
      // holla: '👋 Holla at',
      // buzz: '🐝 Buzz',
      // serenade: '🎼 Serenade',
      // sing: '🎵 Sing a song to',
      // pigeon: '🐦 Send a pigeon to',
      // dropLine: '💬 Drop a line to',
      // converse: '💬 Converse with',
      // sonnet: '📝 Write a sonnet for',
      // telepathy: '🧠 Telepathically reach',
      // vibes: '✨ Send vibes to',
      // beam: '📡 Beam a signal to',
      // raven: '🐦‍⬛ Dispatch a raven to',
      // smoke: '🔥 Send smoke signals to',
      // touchGrass: '🌿 Touch grass with',
      // party: '🎉 Party with',
      // gauntlet: '🏆 Run the gauntlet with',
      // duel: '🤺 Duel with',
      // memories: '💭 Make memories with',
      // brainstorm: '💡 Brainstorm with',
      // dance: '💃 Dance with',
      // cake: '🎂 Bake a cake with',
    },

    externalLink: {
      title: 'External Link',
      description:
        'You are about to open a link that will take you outside of Adieuu. Please verify you trust this destination before continuing.',
      destination: 'Destination',
      trackingDetected: 'Tracking parameters detected',
      trackingHint:
        'This URL contains parameters commonly used for cross-site tracking ({{params}}). You can open the link without them to reduce fingerprinting.',
      trustDomain: "Don't warn me again for {{domain}}",
      trustAll: "Don't warn me for any external links",
      openClean: 'Open without tracking',
      openConfirm: 'Open Anyway',
    },

    embeds: {
      showPreview: 'Show link preview',
      enableAll: 'Enable All Embeds',
      enableAllPrompt: 'Enable all embeds',
      enableAllTitle: 'Enable All Embeds',
      enableAllDescription:
        'When embeds are enabled, your browser loads images directly from third-party websites. This means those sites can see your IP address and may use this to track when you view a message containing their link.',
      enableAllNote:
        'You can change this at any time in your Appearance settings, or choose to allow embeds only from specific domains.',
      goBack: 'Go back',
      alwaysShowFromSite: 'Always show embeds from {{domain}}',
    },

    ttlOff: 'Disappear this message',
    ttlActive: 'Message will disappear after {{ttl}}',

    filter: {
      button: 'Filter',
      tooltip: 'Filter your list by type, sort order, and archived items',
      typeAll: 'All',
      typeDms: 'DMs',
      typeGroups: 'Groups',
      sortRecent: 'Recent',
      sortAlpha: 'A\u2013Z',
      showArchived: 'Show archived',
    },

    favorites: {
      section: 'Favourites',
    },

    archiveToast: 'Conversation archived',
    unarchiveToast: 'Conversation unarchived',
    favoriteAddedToast: 'Added to favourites',
    favoriteRemovedToast: 'Removed from favourites',

    /** Outbox send blocked (e.g. other user blocked you). */
    sendBlocked: 'Message could not be sent',

    mediaOutbox: {
        panelTitle: 'Pending media sends',
        closePanel: 'Close',
        empty: 'No pending uploads for this chat.',
        toolbarAria: 'Pending media uploads',
        toolbarTitle: 'Pending uploads',
        cancel: 'Cancel send',
        retry: 'Retry',
        dismiss: 'Dismiss',
        stageQueued: 'Queued',
        stagePreparing: 'Preparing media…',
        stageEncrypting: 'Encrypting…',
        stageUploading: 'Uploading…',
        stageSending: 'Sending message…',
        stageScan: 'Safety scan uploading…',
        stageFailed: 'Failed',
        stageCancelled: 'Cancelled',
    },

    messageSearch: {
        title: 'Search messages',
        placeholder: 'Search…',
        close: 'Close',
        newSearch: 'New search',
        startSearch: 'Start search',
        modifySearch: 'Modify search',
        endSearch: 'End search',
        recentSearches: 'Recent searches',
        recentSearchesShort: 'Recent Searches',
        recentNoKeywords: 'Any text',
        adminNoPersistent:
            'This conversation requires that local search data is not kept after you close search.',
        filterHasReplies: 'Has replies',
        filterRepliesOnly: 'Replies only',
        filterHasAttachments: 'Has attachments',
        filterAuthor: 'Author',
        filterAuthorAll: 'Anyone',
        timeRangeLabel: 'Time range',
        timeRange7d: 'Last 7 days',
        timeRange14d: 'Last 2 weeks',
        timeRange30d: 'Last month',
        timeRange90d: 'Last 3 months',
        timeRange180d: 'Last 6 months',
        timeRange365d: 'Last year',
        timeRangeAll: 'All time',
        loading: 'Loading…',
        searching: 'Searching…',
        searchComplete: 'Search complete',
        searchPaused: 'Paused',
        searchStatusLineOne:
            '{{time}} · 1 result found across {{indexedCount, number}} messages indexed',
        searchStatusLineMany:
            '{{time}} · {{resultCount, number}} results found across {{indexedCount, number}} messages indexed',
        pauseSearch: 'Pause search',
        searchPausedHint: 'Resume to keep loading older messages and update results.',
        resumeSearch: 'Resume search',
        sortLabel: 'Sort',
        sortNewestFirst: 'Newest first',
        sortOldestFirst: 'Oldest first',
        resultsListAria: 'Search results',
        noResults: 'No messages match.',
        toolbarAria: 'Search messages',
        settingsDisallowTitle: 'Disallow persistent local search cache',
        settingsDisallowHint:
            'Members cannot keep decrypted message text on their device for search after they close search. Search still works during an active search session.',
    },

    // Moderation skip
    enableModeration: 'Enable content moderation',
    enableModerationTooltip:
        'Uncheck to opt-out of anonymized moderation scanning for these files (e.g. for files with sensitive personal data).\n\nAbnormal volumes of unmoderated content may result in throttling/caps for your Alias and other conversation members: we want to give you privacy (which is why we offer an opt-out), but we don\'t want to support illegal content.',
    allowSkipModeration: 'Allow members to skip moderation',
    allowSkipModerationHint:
        'Members can choose to skip content moderation scanning when sending media. Recipients may hide unmoderated content.',
    audioCallsEnabled: 'Allow audio calls',
    audioCallsEnabledHint: 'When disabled, participants cannot start or join audio calls.',
    videoCallsEnabled: 'Allow video calls',
    videoCallsEnabledHint: 'When disabled, participants cannot enable video in calls.',
    screenshareEnabled: 'Allow screen sharing',
    screenshareEnabledHint: 'When disabled, participants cannot share their screen during calls.',
    unmoderatedMediaHidden: 'Content skipped moderation',
    showUnmoderatedMedia: 'Show anyway',
    unmoderatedMediaTitle: 'Unmoderated Media',
    unmoderatedMediaDescription:
        'Control whether media that skipped moderation scanning is automatically displayed or hidden behind a placeholder.',
    unmoderatedMediaAllow: 'Display all media (including unmoderated)',
    unmoderatedMediaHide: 'Hide unmoderated media behind a placeholder',

    // Conversation folders
    folders: {
        newFolder: 'New Folder',
        rename: 'Edit Folder',
        removeFolder: 'Remove Folder',
        addFavorite: 'Add to Favorites',
        removeFavorite: 'Remove from Favorites',
        removeFromFolder: 'Remove from Folder',
        iconDynamic: 'Dynamic',
        iconCustom: 'Custom Icon',
        colorLabel: 'Icon Color',
        nameLabel: 'Folder Name',
        namePlaceholder: 'Enter a name…',
        save: 'Save',
        cancel: 'Cancel',
        folderCreatedToast: 'Folder created',
        folderDeletedToast: 'Folder removed',
        folderUpdatedToast: 'Folder updated',
        favoriteAddedToast: 'Folder added to favorites',
        favoriteRemovedToast: 'Folder removed from favorites',
        close: 'Close folder panel',
        emptyFolder: 'Nothing in this folder',
        dmCount: '{{count}} DM',
        dmCount_one: '1 DM',
        dmCount_other: '{{count}} DMs',
        groupCount: '{{count}} group',
        groupCount_one: '1 group',
        groupCount_other: '{{count}} groups',
        spaceCount: '{{count}} Space',
        spaceCount_one: '1 Space',
        spaceCount_other: '{{count}} Spaces',
        emptyCount: 'Empty folder',
        folderCreateErrorToast: 'Failed to create folder',
    },
} as const;
